import { Alert } from "antd";
import dayjs from "dayjs";

import {
  CURRENT_OFFER_FALLBACK_PATH,
  type CurrentOfferAudience,
  type CurrentOfferPickFailure,
  type CurrentOfferResolution,
} from "@fitness-app/data-models/domain/currentOffer/resolveCurrentOffer";

export const CURRENT_OFFER_LINKS: Record<CurrentOfferAudience, string> = {
  new: "/aktualna-oferta",
  renewal: "/aktualna-oferta-przedluzenie",
};

/** Why the pick is not linked; also the tooltip of a variant that cannot be picked. */
export const currentOfferFailureLabel = (failure: CurrentOfferPickFailure, audience: CurrentOfferAudience): string => {
  switch (failure) {
    case "notConfigured":
      return "Nie wybrano promocji.";
    case "variantMissing":
      return "Wybrany wariant już nie istnieje.";
    case "inactive":
      return "Wariant jest nieaktywny.";
    case "wrongTarget":
      return audience === "renewal"
        ? "Link dla obecnych klientek prowadzi tylko do koszyków przedłużenia."
        : "Wariant jest dla przedłużeń, a ten link trafia do nowych klientek.";
    case "excluded":
      return "Wariant ma włączone „Nie proponuj wariantu”.";
    case "manualPayment":
      return "Wariant ma przelew tradycyjny (-manual albo przelew dla wszystkich cen).";
    case "expired":
      return "Wariant wygasł.";
  }
};

const formatUntil = (activeUntil: number | null) =>
  activeUntil ? `do ${dayjs.unix(activeUntil).format("YYYY-MM-DD HH:mm")}` : "bez daty końca, do ręcznej zmiany";

/** Where the link leads today, computed with the same rule as the e-commerce page. */
export const CurrentOfferStatus = ({
  audience,
  resolution,
}: {
  audience: CurrentOfferAudience;
  resolution: CurrentOfferResolution;
}) => {
  const link = CURRENT_OFFER_LINKS[audience];

  if (resolution.type === "offer" && !resolution.pickFailure) {
    return (
      <Alert
        type="success"
        showIcon
        message={`Link ${link} prowadzi do ${resolution.path} (${formatUntil(resolution.activeUntil)}).`}
      />
    );
  }

  if (resolution.type === "offer" && resolution.pickFailure) {
    return (
      <Alert
        type={resolution.pickFailure === "notConfigured" ? "info" : "warning"}
        showIcon
        message={`Link ${link} prowadzi do domyślnego koszyka przedłużenia ${resolution.path} (${formatUntil(resolution.activeUntil)}).`}
        description={currentOfferFailureLabel(resolution.pickFailure, audience)}
      />
    );
  }

  return (
    <Alert
      type={resolution.type === "fallback" && resolution.reason === "notConfigured" ? "info" : "error"}
      showIcon
      message={`Link ${link} prowadzi na ${CURRENT_OFFER_FALLBACK_PATH}.`}
      description={resolution.type === "fallback" ? currentOfferFailureLabel(resolution.reason, audience) : undefined}
    />
  );
};
